import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import callApi, { abortableCallApi } from 'utils/callApi';
import { LOCATION_CHANGE } from 'react-router-redux';
import { uniqBy } from 'lodash';
import orderStatusesAutoChange from 'config/orderStatusesAutoChange';
import { requestWorkflowBulk } from 'redux/modules/bulk';

import firstCharToUpperCase from 'utils/firstCharToUpperCase';

import { fetchAccountInformationSuccess } from 'redux/modules/account';
import { fetchAppInfoSuccess } from 'redux/modules/appInfo';
import {
  FETCH_PATIENT_INSURANCE_SUCCESS,
  UPDATE_PATIENT_INSURANCE_SUCCESS,
  CHECK_PATIENT_INSURANCE_ELIGIBILITY,
  CHECK_PATIENT_INSURANCE_ELIGIBILITY_SUCCESS,
  CHECK_PATIENT_INSURANCE_ELIGIBILITY_FAIL,
} from 'redux/modules/patientsInsurance';
import {
  UPDATE_PATIENT_SUCCESS,
  ADD_PATIENT_RESOURCE_SUCCESS,
  REMOVE_PATIENT_RESOURCE_SUCCESS,
  CHANGE_PATIENT_RESOURCE_SUCCESS,
} from 'redux/modules/patients';
import { CREATE_PATIENT_VITALS_SUCCESS } from 'redux/modules/patientsVitals';
import { addNotification } from 'redux/modules/notifications';
import { saveRecoverOrder } from 'redux/modules/recoverOrder';
import { requestUpdateOrderLine } from 'redux/modules/ordersLines';

// Actions
export const FETCH_ORDERS = 'FETCH_ORDERS';
export const FETCH_ORDERS_SUCCESS = 'FETCH_ORDERS_SUCCESS';
export const FETCH_ORDERS_FAIL = 'FETCH_ORDERS_FAIL';
export const CLEAR_ORDERS = 'CLEAR_ORDERS';
export const FETCH_ORDER = 'FETCH_ORDER';
export const FETCH_ORDER_SUCCESS = 'FETCH_ORDER_SUCCESS';
export const FETCH_ORDER_FAIL = 'FETCH_ORDER_FAIL';
export const CREATE_ORDER = 'CREATE_ORDER';
export const CREATE_ORDER_SUCCESS = 'CREATE_ORDER_SUCCESS';
export const CREATE_ORDER_FAIL = 'CREATE_ORDER_FAIL';
export const UPDATE_ORDER = 'UPDATE_ORDER';
export const UPDATE_ORDER_SUCCESS = 'UPDATE_ORDER_SUCCESS';
export const UPDATE_ORDER_FAIL = 'UPDATE_ORDER_FAIL';
export const BULK_UPDATE_ORDERS = 'BULK_UPDATE_ORDERS';
export const BULK_UPDATE_ORDERS_SUCCESS = 'BULK_UPDATE_ORDERS_SUCCESS';
export const BULK_UPDATE_ORDERS_FAIL = 'BULK_UPDATE_ORDERS_FAIL';
export const DELETE_ORDER = 'DELETE_ORDER';
export const DELETE_ORDER_SUCCESS = 'DELETE_ORDER_SUCCESS';
export const DELETE_ORDER_FAIL = 'DELETE_ORDER_FAIL';
export const ADD_ORDER_RESOURCE = 'ADD_ORDER_RESOURCE';
export const ADD_ORDER_RESOURCE_SUCCESS = 'ADD_ORDER_RESOURCE_SUCCESS';
export const ADD_ORDER_RESOURCE_FAIL = 'ADD_ORDER_RESOURCE_FAIL';
export const REMOVE_ORDER_RESOURCE = 'REMOVE_ORDER_RESOURCE';
export const REMOVE_ORDER_RESOURCE_SUCCESS = 'REMOVE_ORDER_RESOURCE_SUCCESS';
export const REMOVE_ORDER_RESOURCE_FAIL = 'REMOVE_ORDER_RESOURCE_FAIL';
export const CHANGE_ORDER_RESOURCE = 'CHANGE_ORDER_RESOURCE';
export const CHANGE_ORDER_RESOURCE_SUCCESS = 'CHANGE_ORDER_RESOURCE_SUCCESS';
export const CHANGE_ORDER_RESOURCE_FAIL = 'CHANGE_ORDER_RESOURCE_FAIL';
export const REMOVE_ORDER_INSURANCE_PARAMS = 'REMOVE_ORDER_INSURANCE_PARAMS';
export const REMOVE_ORDER_INSURANCE_PARAMS_SUCCESS = 'REMOVE_ORDER_INSURANCE_PARAMS_SUCCESS';
export const REMOVE_ORDER_INSURANCE_PARAMS_FAIL = 'REMOVE_ORDER_INSURANCE_PARAMS_FAIL';

// Reducer
export default function ordersReducer(state = {
  data: [], count: 0, loading: 0, contextFiltersOptions: null,
}, action) {
  const { type } = action;
  let triggerLoading;
  let payload;
  if (action.payload) {
    ({ triggerLoading, ...payload } = action.payload);
  }
  switch (type) {
    case FETCH_ORDERS:
      return {
        ...state,
        ...triggerLoading && state.loading <= 0 ? { loading: state.loading + 1 } : {},
      };
    case FETCH_ORDERS_SUCCESS: {
      const newData = payload.data.map(dataItem => ({
        ...dataItem,
        'Order Line': dataItem['Order Line'].map(item => ({
          ...item,
          'Transmission Method': dataItem['Transmission Method'],
          'Order ID': item['Order ID'] || dataItem.ID,
        })),
        Patient: {
          ...dataItem.Patient,
          Diagnosis: dataItem.Patient.Diagnosis || [],
          Documents: dataItem.Patient.Documents || [],
          'Credit Card': dataItem.Patient['Credit Card'] || [],
          'Insurance Information': dataItem.Patient['Insurance Information'] || [],
          Prescriber: dataItem.Patient.Prescriber || [],
          'Referral Source': dataItem.Patient['Referral Source'] || [],
          'Sales Representative': dataItem.Patient['Sales Representative'] || [],
          Vitals: dataItem.Patient.Vitals || [],
        },
      }));
      return {
        ...state,
        ...payload,
        ...payload.push ? { data: [
          ...state.data.slice(0, state.data.length),
          ...newData.slice(0, payload.data.length),
        ] } : { data: newData },
        ...payload.contextFiltersOptions && Object.keys(payload.contextFiltersOptions).length > 0 ? {
          contextFiltersOptions: {
            users: Object.keys(payload.contextFiltersOptions['Assigned User ID'])
              .map(key => ({
                'First Name': payload.contextFiltersOptions['Assigned User ID'][key].first_name,
                'Last Name': payload.contextFiltersOptions['Assigned User ID'][key].last_name,
                ID: Number(key),
                count: payload.contextFiltersOptions['Assigned User ID'][key].count,
              })),
            drivers: Object.keys(payload.contextFiltersOptions['Driver ID'])
              .map(key => ({
                'First Name': Number(key)
                  ? payload.contextFiltersOptions['Driver ID'][key].first_name
                  : 'Driver',
                'Last Name': Number(key)
                  ? payload.contextFiltersOptions['Driver ID'][key].last_name
                  : 'not assigned',
                ID: Number(key) ? Number(key) : null,
                count: payload.contextFiltersOptions['Driver ID'][key].count,
              })),
            statuses: Object.keys(payload.contextFiltersOptions['Workflow Status ID'])
              .map(key => ({
                name: payload.contextFiltersOptions['Workflow Status ID'][key].workflow_status_name,
                ID: Number(key),
                count: payload.contextFiltersOptions['Workflow Status ID'][key].count,
              })),
            orderLineTypes: Object.keys(payload.contextFiltersOptions['Line Type'])
              .map(key => ({
                name: key, count: payload.contextFiltersOptions['Line Type'][key].count,
              })),
            insurances: Object.keys(payload.contextFiltersOptions['Insurance Info ID'])
              .filter(key => key !== 'null')
              .map(key => ({
                'Payer Name': key !== 'null'
                  ? payload.contextFiltersOptions['Insurance Info ID'][key].payer_name
                  : 'Self Payer',
                ID: Number(key),
                count: payload.contextFiltersOptions['Insurance Info ID'][key].count,
              })),
            cities: Object.keys(payload.contextFiltersOptions.City)
              .map((key) => ({
                name: key === 'null' ? 'No Patient Address' : key,
                count: payload.contextFiltersOptions.City[key].count,
              })),
            sales: Object.keys(payload.contextFiltersOptions['Sales Representative ID'])
              .map(key => ({
                'First Name': Number(key)
                  ? payload.contextFiltersOptions['Sales Representative ID'][key].first_name
                  : 'Sales',
                'Last Name': Number(key)
                  ? payload.contextFiltersOptions['Sales Representative ID'][key].last_name
                  : 'not assigned',
                ID: Number(key) ? Number(key) : null,
                count: payload.contextFiltersOptions['Sales Representative ID'][key].count,
              })),
            referralSources: Object.keys(payload.contextFiltersOptions['Referral Source ID'])
              .map(key => ({
                name: Number(key)
                  ? payload.contextFiltersOptions['Referral Source ID'][key].name
                  : 'Referral not assigned',
                ID: Number(key) ? Number(key) : null,
                count: payload.contextFiltersOptions['Referral Source ID'][key].count,
              })),
            prescribers: Object.keys(payload.contextFiltersOptions['Prescriber ID'])
            .map(key => ({
              'First Name': Number(key)
                  ? payload.contextFiltersOptions['Prescriber ID'][key].first_name
                  : 'Prescriber',
              'Last Name': Number(key)
                ? payload.contextFiltersOptions['Prescriber ID'][key].last_name
                : 'not assigned',
              ID: Number(key) ? Number(key) : null,
              count: payload.contextFiltersOptions['Prescriber ID'][key].count,
            })),
            hcpcsCodes: Object.keys(payload.contextFiltersOptions['HCPC S Code ID'])
              .map(key => ({
                name: payload.contextFiltersOptions['HCPC S Code ID'][key].name,
                ID: Number(key),
                count: payload.contextFiltersOptions['HCPC S Code ID'][key].count,
              })),
            watchers: Object.keys(payload.contextFiltersOptions['Watcher ID'])
              .map(key => ({
                'First Name': payload.contextFiltersOptions['Watcher ID'][key].first_name || 'No',
                'Last Name': payload.contextFiltersOptions['Watcher ID'][key].last_name || 'Watcher',
                ID: Number(key),
                count: payload.contextFiltersOptions['Watcher ID'][key].count,
              })),
          },
        } : {
          contextFiltersOptions: state.contextFiltersOptions,
        },
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    }
    case FETCH_ORDERS_FAIL:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case CLEAR_ORDERS:
      return {
        ...state,
        data: [],
        count: 0,
      };
    case FETCH_ORDER:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading + 1 } : {},
      };
    case FETCH_ORDER_SUCCESS:
    case CREATE_ORDER_SUCCESS: {
      let isPresent = false;
      let newCount = state.count;
      const newData = state.data.map((dataItem) => {
        if (dataItem.ID !== payload.ID) {
          return dataItem;
        }
        isPresent = true;
        return {
          ...dataItem,
          ...payload,
          'Order Line': payload['Order Line'].map(item => ({
            ...item,
            'Transmission Method': payload['Transmission Method'],
          })),
        };
      });
      if (!isPresent) {
        newCount += 1;
        newData.push({
          ...payload,
          'Order Line': payload['Order Line'].map(item => ({
            ...item,
            'Transmission Method': payload['Transmission Method'],
          })),
        });
      }
      return {
        ...state,
        count: newCount,
        data: newData,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    }
    case FETCH_ORDER_FAIL:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case UPDATE_ORDER_SUCCESS:
    case ADD_ORDER_RESOURCE_SUCCESS:
    case CHANGE_ORDER_RESOURCE_SUCCESS:
    case REMOVE_ORDER_RESOURCE_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        if (dataItem.ID !== payload.ID) {
          return dataItem;
        }
        return {
          ...dataItem,
          ...payload,
          ...payload['Order Line'] ? {
            'Order Line': payload['Order Line'].map(item => ({
              ...item,
              'Transmission Method': payload['Transmission Method'],
            })),
          } : {
            'Order Insurance Param': uniqBy([
              ...payload['Order Insurance Param'],
              ...dataItem['Order Insurance Param'],
            ], 'ID'),
          },
        };
      });
      return {
        ...state,
        data: newData,
      };
    }
    case BULK_UPDATE_ORDERS_SUCCESS: {
      let deletedOrdersCount = 0;
      const newData = state.data.map((dataItem) => {
        const newDataItem = payload.data[dataItem.ID];
        if (newDataItem) {
          if (newDataItem['Is Delete']) {
            deletedOrdersCount = ++deletedOrdersCount;
            return null;
          }
          return newDataItem;
        }

        return dataItem;
      });
      return {
        ...state,
        data: newData.filter(item => item),
        count: state.count - deletedOrdersCount,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    }
    case DELETE_ORDER:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading + 1 } : {},
      };
    case DELETE_ORDER_SUCCESS: {
      const index = state.data.findIndex(dataItem => dataItem.ID === payload.ID);
      const newData = [
        ...state.data.slice(0, index),
        ...state.data.slice(index + 1),
      ];
      return {
        ...state,
        count: state.count - 1,
        data: newData,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    }
    case DELETE_ORDER_FAIL:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case FETCH_PATIENT_INSURANCE_SUCCESS:
    case UPDATE_PATIENT_INSURANCE_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        if (!dataItem['Primary Insurance']
          || dataItem.Insurance.length === 0) {
          return dataItem;
        }
        const newInsurances = dataItem.Insurance.map((insurancesItem) => {
          if (insurancesItem.ID !== payload.ID) {
            return insurancesItem;
          }

          return {
            ...insurancesItem,
            ...payload,
          };
        });

        return {
          ...dataItem,
          Insurance: newInsurances,
          ...dataItem['Primary Insurance'].ID === payload.ID ? {
            'Primary Insurance': {
              ...dataItem['Primary Insurance'],
              ...payload,
            },
          } : {},
        };
      });
      return {
        ...state,
        count: state.count,
        data: newData,
      };
    }
    case CHECK_PATIENT_INSURANCE_ELIGIBILITY: {
      const newData = state.data.map((dataItem) => {
        if (!dataItem['Primary Insurance']
          || dataItem.Insurance.length === 0) {
          return dataItem;
        }
        const newInsurances = dataItem.Insurance.map((insurancesItem) => {
          if (insurancesItem.ID !== payload['Insurance ID']) {
            return insurancesItem;
          }

          return {
            ...insurancesItem,
            'Checking Eligibility': true,
          };
        });
        return {
          ...dataItem,
          Insurance: newInsurances,
          ...dataItem['Primary Insurance'].ID === payload['Insurance ID'] ? {
            'Primary Insurance': {
              ...dataItem['Primary Insurance'],
              'Checking Eligibility': true,
            },
          } : {},
        };
      });
      return {
        ...state,
        data: newData,
      };
    }
    case CHECK_PATIENT_INSURANCE_ELIGIBILITY_SUCCESS:
    case CHECK_PATIENT_INSURANCE_ELIGIBILITY_FAIL: {
      const newData = state.data.map((dataItem) => {
        if (!dataItem['Primary Insurance']
          || dataItem.Insurance.length === 0) {
          return dataItem;
        }
        const newInsurances = dataItem.Insurance.map((insurancesItem) => {
          if (insurancesItem.ID !== payload['Insurance ID']) {
            return insurancesItem;
          }

          return {
            ...insurancesItem,
            'Checking Eligibility': false,
          };
        });

        return {
          ...dataItem,
          Insurance: newInsurances,
          ...dataItem['Primary Insurance'].ID === payload['Insurance ID'] ? {
            'Primary Insurance': {
              ...dataItem['Primary Insurance'],
              'Checking Eligibility': false,
            },
          } : {},
        };
      });
      return {
        ...state,
        data: newData,
      };
    }
    case UPDATE_PATIENT_SUCCESS:
    case ADD_PATIENT_RESOURCE_SUCCESS:
    case CHANGE_PATIENT_RESOURCE_SUCCESS:
    case REMOVE_PATIENT_RESOURCE_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        if (dataItem.Patient['Record Number'] !== payload['Record Number']) {
          return dataItem;
        }
        return {
          ...dataItem,
          Patient: payload,
        };
      });
      return {
        ...state,
        data: newData,
      };
    }
    case CREATE_PATIENT_VITALS_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        if (dataItem.Patient['Record Number'] !== payload['Record Number']) {
          return dataItem;
        }
        const newVitals = [
          ...dataItem.Patient.Vitals.slice(0, dataItem.Patient.Vitals.length),
          payload.Vitals,
        ];
        return {
          ...dataItem,
          Patient: {
            ...dataItem.Patient,
            Vitals: newVitals,
          },
        };
      });
      return {
        ...state,
        data: newData,
      };
    }
    case REMOVE_ORDER_INSURANCE_PARAMS_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        const removedInsuranceParamIndex = dataItem['Order Insurance Param']
          .findIndex(insuranceParam => payload.ID.indexOf(insuranceParam.ID) !== -1);
        if (removedInsuranceParamIndex === -1) {
          return dataItem;
        }
        const newInsuranceParams = [
          ...dataItem['Order Insurance Param'].slice(0, removedInsuranceParamIndex),
          ...dataItem['Order Insurance Param'].slice(removedInsuranceParamIndex + 1),
        ];
        return {
          ...dataItem,
          'Order Insurance Param': newInsuranceParams,
        };
      });
      return {
        ...state,
        data: newData,
      };
    }
    case LOCATION_CHANGE:
      return {
        ...state,
        loading: 0,
      };
    default:
      return state;
  }
}

// Action Creators
export function fetchOrders(payload) {
  return { type: FETCH_ORDERS, payload };
}

export function fetchOrdersSuccess(payload) {
  return { type: FETCH_ORDERS_SUCCESS, payload };
}

export function fetchOrdersFail(payload) {
  return { type: FETCH_ORDERS_FAIL, payload };
}

export function clearOrders() {
  return { type: CLEAR_ORDERS };
}

export function fetchOrder(payload) {
  return { type: FETCH_ORDER, payload };
}

export function fetchOrderSuccess(payload) {
  return { type: FETCH_ORDER_SUCCESS, payload };
}

export function fetchOrderFail(payload) {
  return { type: FETCH_ORDER_FAIL, payload };
}

export function createOrder(payload) {
  return { type: CREATE_ORDER, payload };
}

export function createOrderSuccess(payload) {
  return { type: CREATE_ORDER_SUCCESS, payload };
}

export function createOrderFail(payload) {
  return { type: CREATE_ORDER_FAIL, payload };
}

export function updateOrder(payload) {
  return { type: UPDATE_ORDER, payload };
}

export function updateOrderSuccess(payload) {
  return { type: UPDATE_ORDER_SUCCESS, payload };
}

export function updateOrderFail(payload) {
  return { type: UPDATE_ORDER_FAIL, payload };
}

export function bulkUpdateOrders(payload) {
  return { type: BULK_UPDATE_ORDERS, payload };
}

export function bulkUpdateOrdersSuccess(payload) {
  return { type: BULK_UPDATE_ORDERS_SUCCESS, payload };
}

export function bulkUpdateOrdersFail(payload) {
  return { type: BULK_UPDATE_ORDERS_FAIL, payload };
}

export function deleteOrder(payload) {
  return { type: DELETE_ORDER, payload };
}

export function deleteOrderSuccess(payload) {
  return { type: DELETE_ORDER_SUCCESS, payload };
}

export function deleteOrderFail(payload) {
  return { type: DELETE_ORDER_FAIL, payload };
}

export function addOrderResource(payload) {
  return { type: ADD_ORDER_RESOURCE, payload };
}

export function addOrderResourceSuccess(payload) {
  return { type: ADD_ORDER_RESOURCE_SUCCESS, payload };
}

export function addOrderResourceFail(payload) {
  return { type: ADD_ORDER_RESOURCE_FAIL, payload };
}

export function removeOrderResource(payload) {
  return { type: REMOVE_ORDER_RESOURCE, payload };
}

export function removeOrderResourceSuccess(payload) {
  return { type: REMOVE_ORDER_RESOURCE_SUCCESS, payload };
}

export function removeOrderResourceFail(payload) {
  return { type: REMOVE_ORDER_RESOURCE_FAIL, payload };
}

export function changeOrderResource(payload) {
  return { type: CHANGE_ORDER_RESOURCE, payload };
}

export function changeOrderResourceSuccess(payload) {
  return { type: CHANGE_ORDER_RESOURCE_SUCCESS, payload };
}

export function changeOrderResourceFail(payload) {
  return { type: CHANGE_ORDER_RESOURCE_FAIL, payload };
}

export function removeOrderInsuranceParams(payload) {
  return { type: REMOVE_ORDER_INSURANCE_PARAMS, payload };
}

export function removeOrderInsuranceParamsSuccess(payload) {
  return { type: REMOVE_ORDER_INSURANCE_PARAMS_SUCCESS, payload };
}

export function removeOrderInsuranceParamsFail(payload) {
  return { type: REMOVE_ORDER_INSURANCE_PARAMS_FAIL, payload };
}

let abort;

// Requests
export function requestFetchOrders({ limit, offset }) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = abortableCallApi(`workflow/order/?limit=${limit}&offset=${offset}`);
  abort = request.abort;
  return request.res;
}

export function requestSearchOrders({
  limit, offset, filters = [], sortBy = [], show_schema_name = 'dashboard_workflow_schema',
}) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = abortableCallApi('workflow/order/search/', 'POST', {
    ...filters ? { filters } : {},
    sort_by: [
      ...sortBy,
      {
        name: 'ID',
        sort: 'asc',
      },
    ],
    limit,
    offset,
    show_schema_name,
  });
  abort = request.abort;
  return request.res;
}

export function requestFetchOrder(payload) {
  return callApi(`workflow/order/?id=${payload}`);
}

export function requestCreateOrder(payload) {
  return callApi('workflow/order/', 'POST', payload);
}

export function requestUpdateOrder(payload) {
  return callApi('workflow/order/', 'PATCH', payload);
}

export function requestDeleteOrder(payload) {
  return callApi(`workflow/order/?id=${payload}`, 'DELETE');
}

export function requestAddOrderResource(payload) {
  return callApi('workflow/order/change_resource/add/', 'POST', payload);
}

export function requestRemoveOrderResource(payload) {
  return callApi('workflow/order/change_resource/remove/', 'POST', payload);
}

export function requestChangeOrderResource(payload) {
  return callApi('workflow/order/change_resource/patch/', 'POST', payload);
}

export function requestCreateOrderInsuranceParam(payload) {
  return callApi('workflow/insurance_order_param/', 'POST', payload);
}

export function requestUpdateOrderInsuranceParam(payload) {
  return callApi('workflow/insurance_order_param/', 'PATCH', payload);
}

export function requestRemoveOrderInsuranceParam(payload) {
  return callApi(`workflow/insurance_order_param/?id=${payload}`, 'DELETE');
}

// Sagas
function* fetchOrdersSaga(action) {
  const { triggerLoading, filters, sortBy, push } = action.payload;
  try {
    const res = filters || sortBy
      ? yield call(requestSearchOrders, action.payload)
      : yield call(requestFetchOrders, action.payload);
    if (res.count === 0) {
      yield put(fetchOrdersSuccess({
        data: res.data,
        count: res.count,
        contextFiltersOptions: res.next_filter_data || null,
        triggerLoading,
      }));
    } else if (push) {
      yield put(fetchOrdersSuccess({
        push: true,
        data: res.data,
        count: res.count,
        contextFiltersOptions: res.next_filter_data || null,
        triggerLoading,
      }));
    } else {
      yield put(fetchOrdersSuccess({
        data: res.data,
        count: res.count,
        contextFiltersOptions: res.next_filter_data || null,
        triggerLoading,
      }));
    }
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
  } catch (err) {
    yield put(fetchOrdersFail({
      ...err.data,
      triggerLoading,
    }));
  }
}

function* fetchOrderSaga(action) {
  const { triggerLoading, ID } = action.payload;
  try {
    const res = yield call(requestFetchOrder, ID);
    if (res.data.ID) {
      yield put(fetchOrderSuccess({
        ...res.data,
        triggerLoading,
      }));
      yield put(fetchAccountInformationSuccess(res.user_info));
      yield put(fetchAppInfoSuccess(res.app_info));
    } else {
      yield put(fetchOrderFail({
        triggerLoading,
      }));
    }
  } catch (err) {
    yield put(fetchOrderFail({
      ...err.data,
      triggerLoading,
    }));
  }
}

function* createOrderSaga(action) {
  const {
    values,
    resolve,
    reject,
    recoverOrderValues,
    showNotification = true,
  } = action.payload;
  try {
    let statusesAutoChangeRes = [];
    const res = yield call(requestCreateOrder, values);
    const statusesAutoChangeOrderLine = res.data['Order Line'].filter(item =>
      orderStatusesAutoChange[item.Status]);
    if (statusesAutoChangeOrderLine.length > 0) {
      statusesAutoChangeRes = yield all(
        statusesAutoChangeOrderLine.map(item => call(requestUpdateOrderLine, {
          ID: item.ID,
          Status: orderStatusesAutoChange[item.Status].autoChangeStatus,
        })),
      );
      statusesAutoChangeRes = statusesAutoChangeRes.map(item => item.data);
    }
    const order = {
      ...res.data,
      'Order Line': uniqBy([
        ...statusesAutoChangeRes,
        ...res.data['Order Line'],
      ], 'ID'),
    };
    yield put(createOrderSuccess(order));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    if (showNotification) {
      yield put(addNotification({
        message: 'New order successfully added',
        variant: 'success',
      }));
    }
    if (resolve) {
      resolve(order);
    }
  } catch (err) {
    if (err.data.error === 'Access Error') {
      yield put(saveRecoverOrder(recoverOrderValues));
    }
    yield put(createOrderFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* bulkUpdateOrdersSaga(action) {
  const {
    values,
    resolve,
    reject,
    showNotification = true,
  } = action.payload;
  try {
    const bulkPayload = Object.keys(values).reduce((bulkPayloadAccumulator, currentOrderID) => {
      let index = 0;
      const currentOrderPayload = Object.keys(values[currentOrderID]).reduce((currentOrderAccumulator, key) => {
        if (key === 'orderUpdate') {
          index = index + 1;
          return {
            ...currentOrderAccumulator,
            [index]: {
              URL: 'workflow/order/',
              METHOD: 'PATCH',
              BODY: values[currentOrderID][key],
            },
          };
        }
        if (key === 'orderDelete') {
          index = index + 1;
          return {
            ...currentOrderAccumulator,
            [index]: {
              URL: `workflow/order/?id=${values[currentOrderID][key]}`,
              METHOD: 'DELETE',
            },
          };
        }
        if (key === 'createOrderLines') {
          const orderLinesPayload = values[currentOrderID][key].reduce((accumulator, currentValue) => {
            index = index + 1;
            return {
              ...accumulator,
              [index]: {
                URL: 'workflow/order_line/',
                METHOD: 'POST',
                BODY: currentValue,
              },
            }
          }, {});
          return {
            ...currentOrderAccumulator,
            ...orderLinesPayload,
          };
        }
        if (key === 'updateOrderLines') {
          const {
            orderLineValues,
            orderLineStatusesAutoChangeValues,
            checkNumberValues,
            orderLineInsuranceParamValues,
          } = values[currentOrderID][key].reduce((accumulator, currentValue) => {
            let currentOrderLineValues = accumulator.orderLineValues;
            let currentOrderLineStatusesAutoChangeValues = accumulator.orderLineStatusesAutoChangeValues;
            let currentCheckNumberValues = accumulator.checkNumberValues;
            let currentOrderLineInsuranceParamValues = accumulator.orderLineInsuranceParamValues;
            if (currentValue['Check Book Sheet']) {
              const newCheckNumberValues = Object.keys(currentValue['Check Book Sheet']).map(item => ({
                'Order Line ID': Number(currentValue.ID),
                'Insurance ID': Number(item.replace('insuranceID-', '')),
                ...currentValue['Check Book Sheet'][item]['Check Number']
                  ? { 'Check Number': currentValue['Check Book Sheet'][item]['Check Number'] } : {},
                ...currentValue['Check Book Sheet'][item]['Check Book Sheet ID'] !== undefined
                  && !currentValue['Check Book Sheet'][item]['Check Number']
                  ? { 'Check Book Sheet ID': currentValue['Check Book Sheet'][item]['Check Book Sheet ID'] } : {},
              }));
              currentCheckNumberValues = [
                ...currentCheckNumberValues,
                ...newCheckNumberValues,
              ];
              const currentValueWithoutCheckNumber = Object.assign({}, currentValue);
              delete currentValueWithoutCheckNumber['Check Book Sheet'];
              delete currentValueWithoutCheckNumber.orderLineInsuranceParam;
              if (Object.keys(currentValueWithoutCheckNumber).length > 1) {
                currentOrderLineValues = [
                  ...currentOrderLineValues,
                  currentValueWithoutCheckNumber,
                  ...currentValueWithoutCheckNumber.Status
                    && orderStatusesAutoChange[currentValueWithoutCheckNumber.Status] ? [{
                      ID: currentValueWithoutCheckNumber.ID,
                      Status: orderStatusesAutoChange[currentValueWithoutCheckNumber.Status]
                        .autoChangeStatus,
                    }] : [],
                ];
              }
            } else if (currentValue.orderLineInsuranceParam) {
              currentOrderLineInsuranceParamValues = [
                ...currentOrderLineInsuranceParamValues,
                ...Object.keys(currentValue.orderLineInsuranceParam).map(item => ({
                  'Order Line ID': currentValue.ID,
                  'Insurance ID': Number(item.replace('insuranceID-', '')),
                  ...currentValue.orderLineInsuranceParam[item].ID
                    ? { ID: currentValue.orderLineInsuranceParam[item].ID } : {},
                  ...currentValue.orderLineInsuranceParam[item].Reimbs !== undefined
                    ? { Reimbs: currentValue.orderLineInsuranceParam[item].Reimbs } : {},
                  ...currentValue.orderLineInsuranceParam[item]['Reimbs Date'] !== undefined
                    ? { 'Reimbs Date': currentValue.orderLineInsuranceParam[item]['Reimbs Date'] } : {},
                })),
              ];
            } else { 
              if (currentValue['Next Workflow Status ID']) {
                currentOrderLineStatusesAutoChangeValues = [
                  ...currentOrderLineStatusesAutoChangeValues,
                  {
                    ID: currentValue.ID,
                    'Workflow Status ID': currentValue['Next Workflow Status ID'],
                  },
                ];
                delete currentValue['Next Workflow Status ID'];
              }
              currentOrderLineValues = [
                ...currentOrderLineValues,
                currentValue,
              ];
            }
            return {
              orderLineValues: currentOrderLineValues,
              orderLineStatusesAutoChangeValues: currentOrderLineStatusesAutoChangeValues,
              checkNumberValues: currentCheckNumberValues,
              orderLineInsuranceParamValues: currentOrderLineInsuranceParamValues,
            };
          }, {
            orderLineValues: [],
            orderLineStatusesAutoChangeValues: [],
            checkNumberValues: [],
            orderLineInsuranceParamValues: [],
          });
          const orderLinesPayload = {
            ...checkNumberValues
              .reduce((accumulator, currentValue) => {
                index = index + 1;
                return {
                  ...accumulator,
                  [index]: {
                    URL: currentValue['Check Number']
                      ? 'workflow/check_book_sheet_attach/add/'
                      : 'workflow/check_book_sheet_attach/remove/',
                    METHOD: 'POST',
                    BODY: currentValue,
                  },
                };
              }, {}),
            ...orderLineValues
              .reduce((accumulator, currentValue) => {
                index = index + 1;
                return {
                  ...accumulator,
                  [index]: {
                    URL: 'workflow/order_line/',
                    METHOD: 'PATCH',
                    BODY: currentValue,
                  },
                }
              }, {}),
            ...orderLineStatusesAutoChangeValues
              .reduce((accumulator, currentValue) => {
                index = index + 1;
                return {
                  ...accumulator,
                  [index]: {
                    URL: 'workflow/order_line/',
                    METHOD: 'PATCH',
                    BODY: currentValue,
                  },
                };
              }, {}),
            ...orderLineInsuranceParamValues
              .reduce((accumulator, currentValue) => {
                index = index + 1;
                return (currentValue.ID && !currentValue.
                Reimbs && !currentValue['Reimbs Date']) ? ({
                  ...accumulator,
                  [index]: {
                    URL: `workflow/order_line_insurance_param/?id=${currentValue.ID}`,
                    METHOD: 'DELETE',
                  },
                }) : ({
                  ...accumulator,
                  [index]: {
                    URL: 'workflow/order_insurance_param/',
                    METHOD: currentValue.ID ? 'PATCH' : 'POST',
                    BODY: currentValue,
                  },
                });
              }, {}),
          };
          return {
            ...currentOrderAccumulator,
            ...orderLinesPayload,
          };
        }
        if (key === 'deleteOrderLines') {
          const orderLinesPayload = values[currentOrderID][key].ID.reduce((accumulator, currentValue) => {
            index = index + 1;
            return {
              ...accumulator,
              [index]: {
                URL: `workflow/order_line/?id=${currentValue}`,
                METHOD: 'DELETE',
              },
            }
          }, {});
          return {
            ...currentOrderAccumulator,
            ...orderLinesPayload,
          };
        }
        if (key === 'deleteOrderLineDocument') {
          const orderLinesPayload = values[currentOrderID][key].ID.reduce((accumulator, currentValue) => {
            index = index + 1;
            return {
              ...accumulator,
              [index]: {
                URL: `workflow/cmn_document/?id=${currentValue}`,
                METHOD: 'DELETE',
              },
            }
          }, {});
          return {
            ...currentOrderAccumulator,
            ...orderLinesPayload,
          };
        }
        if (key === 'addOrderResource') {
          index = index + 1;
          return {
            ...currentOrderAccumulator,
            [index]: {
              URL: 'workflow/order/change_resource/add/',
              METHOD: 'POST',
              BODY: values[currentOrderID][key],
            },
          };
        }
        if (key === 'changeOrderResource') {
          index = index + 1;
          return {
            ...currentOrderAccumulator,
            [index]: {
              URL: 'workflow/order/change_resource/patch/',
              METHOD: 'POST',
              BODY: values[currentOrderID][key],
            },
          };
        }
        if (key === 'removeOrderResource') {
          index = index + 1;
          return {
            ...currentOrderAccumulator,
            [index]: {
              URL: 'workflow/order/change_resource/remove/',
              METHOD: 'POST',
              BODY: values[currentOrderID][key],
            },
          };
        }
        if (key === 'createOrderInsuranceParams') {
          const insuranceParamPayload = values[currentOrderID][key].reduce((accumulator, currentValue) => {
            index = index + 1;
            return {
              ...accumulator,
              [index]: {
                URL: 'workflow/order_insurance_param/',
                METHOD: 'POST',
                BODY: currentValue,
              },
            }
          }, {});
          return {
            ...currentOrderAccumulator,
            ...insuranceParamPayload,
          };
        }
        if (key === 'updateOrderInsuranceParams') {
          const insuranceParamPayload = values[currentOrderID][key].reduce((accumulator, currentValue) => {
            index = index + 1;
            return {
              ...accumulator,
              [index]: {
                URL: 'workflow/order_insurance_param/',
                METHOD: 'PATCH',
                BODY: currentValue,
              },
            }
          }, {});
          return {
            ...currentOrderAccumulator,
            ...insuranceParamPayload,
          };
        }
        if (key === 'removeOrderInsuranceParams') {
          const insuranceParamPayload = values[currentOrderID][key].reduce((accumulator, currentValue) => {
            index = index + 1;
            return {
              ...accumulator,
              [index]: {
                URL: `workflow/order_insurance_param/?id=${currentValue}`,
                METHOD: 'DELETE',
              },
            }
          }, {});
          return {
            ...currentOrderAccumulator,
            ...insuranceParamPayload,
          };
        }
      }, {});
      return {
        ...bulkPayloadAccumulator,
        [currentOrderID]: currentOrderPayload,
      };
    }, {});
    const res = yield call(requestWorkflowBulk, bulkPayload);
    yield put(bulkUpdateOrdersSuccess(res));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    if (resolve) {
      resolve(res);
    }
    if (showNotification) {
      yield put(addNotification({
        message: 'Order successfully updated',
        variant: 'success',
      }));
    }
  } catch (err) {
    yield put(updateOrderFail(err.data));
    if (showNotification) {
      yield put(addNotification({
        message: err.data.description || 'Failed to update order',
        variant: 'warning',
      }));
    }
    if (reject) {
      reject(err.data);
    }
  }
}

function* deleteOrderSaga(action) {
  const { triggerLoading, ID } = action.payload;
  try {
    const res = yield call(requestDeleteOrder, ID);
    yield put(deleteOrderSuccess({
      ID,
      triggerLoading,
    }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Order successfully deleted',
      variant: 'success',
    }));
  } catch (err) {
    yield put(deleteOrderFail({
      ...err.data,
      triggerLoading,
    }));
    yield put(addNotification({
      message: 'Failed to delete order',
      variant: 'warning',
    }));
  }
}

function* addOrderResourceSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestAddOrderResource, values);
    yield put(addOrderResourceSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(addOrderResourceFail(err.data));
    if (reject) {
      reject(err);
    }
  }
}

function* removeOrderResourceSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestRemoveOrderResource, values);
    yield put(removeOrderResourceSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(removeOrderResourceFail(err.data));
    if (reject) {
      reject(err);
    }
  }
}

function* changeOrderResourceSaga(action) {
  const {
    resolve,
    reject,
    values,
  } = action.payload;
  try {
    const res = yield call(requestChangeOrderResource, values);
    yield put(changeOrderResourceSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(changeOrderResourceFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* removeOrderInsuranceParamsSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield all(
      values.map(item => call(requestRemoveOrderInsuranceParam, item)),
    );
    yield put(removeOrderInsuranceParamsSuccess({ ID: values }));
    yield put(fetchAccountInformationSuccess(res[0].user_info));
    yield put(fetchAppInfoSuccess(res[0].app_info));
    if (resolve) {
      resolve(values);
    }
  } catch (err) {
    yield put(removeOrderInsuranceParamsFail(err.data));
    if (reject) {
      reject(err);
    }
  }
}

export function* ordersSaga() {
  yield takeLatest(FETCH_ORDERS, fetchOrdersSaga);
  yield takeEvery(FETCH_ORDER, fetchOrderSaga);
  yield takeEvery(CREATE_ORDER, createOrderSaga);
  yield takeEvery(BULK_UPDATE_ORDERS, bulkUpdateOrdersSaga);
  yield takeEvery(DELETE_ORDER, deleteOrderSaga);
  yield takeEvery(ADD_ORDER_RESOURCE, addOrderResourceSaga);
  yield takeEvery(REMOVE_ORDER_RESOURCE, removeOrderResourceSaga);
  yield takeEvery(CHANGE_ORDER_RESOURCE, changeOrderResourceSaga);
  yield takeEvery(REMOVE_ORDER_INSURANCE_PARAMS, removeOrderInsuranceParamsSaga);
}



// WEBPACK FOOTER //
// ./src/redux/modules/orders.js